import { useState } from 'react';
import { useFamilies } from './hooks/useFamilies';
import { Text } from '@mantine/core';
import { useDeleteFamily } from './hooks/useDeleteFamily';
import { useNavigate } from 'react-router';
import { useClientsContext } from './context/ClientsContext';
import { Family } from 'src/clients/hooks/useFamily';
import { DataTableColumn, DataTableColumnTextAlign } from 'mantine-datatable';
import { SortableColumn } from 'src/common/components/table/types';
import CreateFamilyFlow from 'src/clients/CreateFamily/CreateFamilyFlow';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { useAdvisors } from './hooks/useAdvisors';
import { useClients } from './hooks/useClients';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { FamilyActionsMenu } from './components/FamilyActionsMenu';
import { formatName } from 'src/utils/nameFormatting';
import { matchesClientSearch } from 'src/utils/clientSearch';
import { Client } from 'src/admin/users/types';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatFamilyMembers, getFamilyMemberClientIds } from './utils';
import { formatShortDate } from 'src/utils/dateFormatting';
import {
  AdvisorFilterSelect,
  getAdvisorSortValue,
  matchesAdvisorFilter,
  matchesAdvisorSearch,
} from './components/AdvisorFilterSelect';

export function FamiliesTable({
  filters,
  searchTerm,
}: {
  filters?: Record<string, string>;
  searchTerm?: string;
}) {
  const { refetch } = useFamilies(filters);
  const [advisorFilter, setAdvisorFilter] = useState<string | null>(null);
  const [familyToDelete, setFamilyToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const {
    deleteFamily,
    error: deleteFamilyError,
    reset: resetDeleteFamily,
    isLoading: isDeletingFamily,
  } = useDeleteFamily();
  const navigate = useNavigate();
  const { editFamily, startFamilyCreation } = useClientsContext();
  const { advisors } = useAdvisors();
  const { isSystemAdmin, isServiceAdmin, isAdvisor } = useCurrentUser();
  const {
    flagEnabledFamilyFlowFrontend,
    flagEnabledIndividualDetailsRework,
    flagEnabledFamilyRoleModel,
    flagEnabledAdvisorSortAndFilter,
    flagEnabledFamilyScreenRework,
    flagEnabledClientSearchFullName,
  } = useFlags();
  const showAdvisorFilter = (isSystemAdmin || isServiceAdmin) && flagEnabledAdvisorSortAndFilter;
  const { clients } = useClients(
    filters?.advisorUserId ? { advisorUserId: filters.advisorUserId } : {},
  );

  const advisorMap = advisors
    ? Object.fromEntries(advisors.map(advisor => [advisor.id, advisor]))
    : {};

  const clientMap = Object.fromEntries(clients.map((client: Client) => [client.id, client]));

  const familyFilterPredicate = (family: Family, search: string): boolean => {
    if (!matchesAdvisorFilter(family.advisorUserId, advisorFilter)) {
      return false;
    }

    if (matchesClientSearch([family.name], search, flagEnabledClientSearchFullName)) return true;

    if (
      !isAdvisor &&
      matchesAdvisorSearch(family.advisorUserId, advisorMap, search.toLowerCase())
    ) {
      return true;
    }

    return getFamilyMemberClientIds(family).some(clientId => {
      const client = clientMap[clientId];
      return (
        client != null &&
        matchesClientSearch(
          [client.firstName, client.lastName],
          search,
          flagEnabledClientSearchFullName,
        )
      );
    });
  };

  const handleDeletePress = (family: { id: string; name: string }) => {
    resetDeleteFamily();
    setFamilyToDelete(family);
  };

  const handleDeleteConfirm = async () => {
    if (!familyToDelete) return;
    await deleteFamily(familyToDelete.id);
    setFamilyToDelete(null);
    refetch();
  };

  // Role-model families can't be edited through the legacy star-shape flow
  // (it wipes members and roles on save), so they use the role-aware edit page.
  const handleEditPress = (id: string) => {
    if (flagEnabledFamilyRoleModel) {
      navigate(`/clients/families/${id}/edit`);
    } else {
      editFamily(id);
    }
  };

  const actionsColumn: DataTableColumn<Family> = {
    accessor: 'actions',
    title: '',
    render: (family: Family) => (
      <FamilyActionsMenu
        family={family}
        onEdit={() => handleEditPress(family.id)}
        onDelete={() => handleDeletePress(family)}
        iconSize={16}
      />
    ),
  };

  const newActionsColumn: SortableColumn<Family> = {
    accessor: 'actions',
    title: '',
    render: (family: Family) => (
      <FamilyActionsMenu
        family={family}
        onEdit={() => handleEditPress(family.id)}
        onDelete={() => handleDeletePress(family)}
        iconSize={20}
        iconColor="var(--mantine-color-black)"
      />
    ),
  };

  const newColumnsBase: SortableColumn<Family>[] = [
    {
      accessor: 'name',
      title: 'Family',
      sortable: true,
      sortKey: 'name',
      sortIconType: 'alpha',
      render: (family: Family) => (
        <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
          {family.name}
        </Text>
      ),
    },
    {
      accessor: 'id',
      title: 'Members',
      sortable: true,
      sortValueGetter: (family: Family) => getFamilyMemberClientIds(family).length,
      sortIconType: 'alpha',
      render: (family: Family) => (
        <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
          {formatFamilyMembers(family, clientMap)}
        </Text>
      ),
    },
    {
      accessor: 'advisor',
      title: 'Advisor',
      sortable: true,
      sortIconType: 'alpha',
      sortValueGetter: (family: Family) => getAdvisorSortValue(family.advisorUserId, advisorMap),
      render: (family: Family) => {
        const advisorUserId = family.advisorUserId;
        if (!advisorUserId)
          return (
            <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
              No advisor
            </Text>
          );
        const advisor = advisorMap[advisorUserId];
        const advisorName = advisor
          ? formatName(advisor.firstName, advisor.lastName)
          : 'No advisor';
        return (
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
            {advisorName}
          </Text>
        );
      },
    },
    {
      accessor: 'createdAt',
      title: 'Last updated',
      sortable: true,
      sortKey: 'createdAt',
      sortIconType: 'date',
      render: (family: Family) => (
        <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
          {formatShortDate(family.createdAt)}
        </Text>
      ),
    },
    newActionsColumn,
  ];

  const legacyColumns: SortableColumn<Family>[] = [
    {
      accessor: 'name',
      title: 'Family name',
      sortable: true,
      sortKey: 'name',
      textAlign: 'left' as DataTableColumnTextAlign,
    },
    {
      accessor: 'id',
      title: '# family members',
      render: (family: Family) => {
        return getFamilyMemberClientIds(family).length;
      },
    },
    {
      accessor: 'createdAt',
      title: 'Date added',
      sortable: true,
      sortKey: 'createdAt',
      textAlign: 'right' as DataTableColumnTextAlign,
      render: (family: Family) => {
        const date = new Date(family.createdAt);
        const formattedDate = date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
        return (
          <Text size="sm" c="gray.7">
            {formattedDate}
          </Text>
        );
      },
    },
    actionsColumn,
  ];

  if (isSystemAdmin || isServiceAdmin) {
    legacyColumns.splice(2, 0, {
      accessor: 'advisor',
      title: 'Advisor',
      ...(flagEnabledAdvisorSortAndFilter && {
        sortable: true,
        sortIconType: 'alpha',
        sortValueGetter: (family: Family) => getAdvisorSortValue(family.advisorUserId, advisorMap),
      }),
      render: (family: Family) => {
        const advisorUserId = family.advisorUserId;
        if (!advisorUserId)
          return (
            <Text size="sm" c="gray.7">
              No advisor
            </Text>
          );
        const advisor = advisorMap[advisorUserId];
        const advisorName = advisor
          ? formatName(advisor.firstName, advisor.lastName)
          : 'No advisor';
        return (
          <Text size="sm" c="gray.7">
            {advisorName}
          </Text>
        );
      },
    });
  }

  const handleRowClick = (family: Family) => {
    if (flagEnabledIndividualDetailsRework && flagEnabledFamilyScreenRework) {
      navigate(`/family/${family.id}/profile`);
    } else {
      navigate(`/clients/families/${family.id}`);
    }
  };

  const deleteModal = (
    <ConfirmDeleteModal
      opened={!!familyToDelete}
      onClose={() => setFamilyToDelete(null)}
      onConfirm={handleDeleteConfirm}
      title="Delete family"
      message={`Are you sure you want to delete the ${familyToDelete?.name} family? This will only delete the family, not the individual members. This action cannot be undone.`}
      error={deleteFamilyError}
      isLoading={isDeletingFamily}
    />
  );

  const startAddFamily = flagEnabledFamilyFlowFrontend
    ? () => navigate('/clients/families/new')
    : startFamilyCreation;

  const familyCreationFlow = flagEnabledFamilyFlowFrontend ? null : <CreateFamilyFlow />;

  if (flagEnabledIndividualDetailsRework) {
    return (
      <>
        {familyCreationFlow}
        <SimpleTable<Family>
          resource="families"
          filters={filters}
          pageSize={9999}
          columns={newColumnsBase}
          variant="new-flow"
          onRowClick={handleRowClick}
          enableSearch={false}
          externalSearchTerm={searchTerm}
          filterPredicate={familyFilterPredicate}
        />
        {deleteModal}
      </>
    );
  }

  return (
    <>
      {familyCreationFlow}
      <SimpleTable<Family>
        title="Families"
        resource="families"
        filters={filters}
        columns={legacyColumns}
        onRowClick={handleRowClick}
        filterPredicate={flagEnabledAdvisorSortAndFilter ? familyFilterPredicate : undefined}
        paginationDependencies={advisorFilter}
        primaryButton={{ label: 'Add family', onClick: startAddFamily }}
        HeaderRow={
          showAdvisorFilter && (
            <AdvisorFilterSelect
              advisors={advisors}
              value={advisorFilter}
              onChange={setAdvisorFilter}
            />
          )
        }
      />
      {deleteModal}
    </>
  );
}
